// Pure cohort lookup: where does a user value sit within the SCF
// percentile distribution for a given (dimension × cohort split)?
//
// Uses log-linear interpolation between anchor percentiles. SCF
// distributions are extremely right-skewed, so linear interpolation
// in dollar-space would systematically misrepresent the upper tail.
// Interpolating in log-space gives smoother, more honest curves.

import type {
  AgeBand,
  CohortDimension,
  CohortSplit,
  IncomeBand,
  PercentileKey,
  PercentileRow,
  ScfDataset,
} from './schema';
import { AGE_BANDS, INCOME_BANDS, PERCENTILE_KEYS, cohortKey } from './schema';

/** Numeric percentile (10..99) corresponding to each PercentileKey. */
const PCT_OF: Record<PercentileKey, number> = {
  p10: 10,
  p25: 25,
  p50: 50,
  p75: 75,
  p90: 90,
  p95: 95,
  p99: 99,
};

export interface LookupResult {
  /** 0–100. 0 indicates ≤ P10; 100 indicates ≥ P99. */
  percentile: number;
  /** True when the value is below the lowest anchor or above the highest. */
  clamped: 'below' | 'above' | null;
}

export function lookupPercentile(value: number, row: PercentileRow): LookupResult {
  // Below the P10 anchor — we can't say more than "<10th".
  if (value <= row.p10) {
    return { percentile: value < row.p10 ? 0 : 10, clamped: value < row.p10 ? 'below' : null };
  }
  // At or above the P99 anchor — we can't say more than ">99th".
  if (value >= row.p99) {
    return { percentile: value > row.p99 ? 100 : 99, clamped: value > row.p99 ? 'above' : null };
  }
  for (let i = 0; i < PERCENTILE_KEYS.length - 1; i++) {
    const lo = PERCENTILE_KEYS[i];
    const hi = PERCENTILE_KEYS[i + 1];
    const loVal = row[lo];
    const hiVal = row[hi];
    if (value >= loVal && value <= hiVal) {
      // Log-space interpolation, guarded against non-positive anchors
      // (RETQLIQ etc. can have P10/P25 = 0 — fall back to linear).
      const t =
        loVal > 0 && hiVal > 0
          ? (Math.log(value) - Math.log(loVal)) / (Math.log(hiVal) - Math.log(loVal))
          : (value - loVal) / Math.max(1, hiVal - loVal);
      const pct = PCT_OF[lo] + t * (PCT_OF[hi] - PCT_OF[lo]);
      return { percentile: pct, clamped: null };
    }
  }
  // Defensive: shouldn't reach here because of the clamps above.
  return { percentile: 50, clamped: null };
}

export function getRow(
  dataset: ScfDataset,
  split: CohortSplit,
  dim: CohortDimension,
): PercentileRow | null {
  const key = cohortKey(split);
  return dataset.cohorts[key]?.[dim] ?? null;
}

// ── User cohort derivation ──────────────────────────────────────────────

export function ageToBand(age: number): AgeBand {
  if (age < 35) return '<35';
  if (age < 45) return '35-44';
  if (age < 55) return '45-54';
  if (age < 65) return '55-64';
  if (age < 75) return '65-74';
  return '75+';
}

export function incomeToBand(income: number): IncomeBand {
  if (income < 25_000) return '<25k';
  if (income < 50_000) return '25-50k';
  if (income < 75_000) return '50-75k';
  if (income < 100_000) return '75-100k';
  if (income < 150_000) return '100-150k';
  if (income < 200_000) return '150-200k';
  return '200k+';
}

// Re-export for callers that only need the band lists from this module.
export { AGE_BANDS, INCOME_BANDS };
